import { css, html } from "lit";
import { customElement } from "lit/decorators/custom-element.js";
import { property } from "lit/decorators/property.js";
import "./text";
import "./title";
import "./icon";
import "./intersection-flip";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { when } from "lit/directives/when.js";
import { Icon } from "../../icons";
import { BsSection } from "./section";
import { ifDefined } from "lit/directives/if-defined.js";

export function isVideo(src: string): boolean {
	return src.endsWith(".mp4");
}

@customElement("bs-section-split")
export class BsSectionSplit extends BsSection {
	static get styles() {
		return [
			...super.styles,
			css`
				:host {
					display: flex;
					flex-direction: column;
					padding: 0 var(--space-l);
				}

				:host([flip]) #container {
					flex-direction: row-reverse;
				}

				:host([hero]) {
					min-height: var(--app-height);
				}

				:host([hero]) #headline {
					--title-font-weight: 900;
					--title-font-size: 50px;
					margin: 0 0 var(--space-s);
				}

				:host([hero]) #text {
					--text-font-size: 26px;
					margin: 0 0 var(--space-l);
					line-height: 1.2;
					opacity: 0.6;
				}

				#icon {
					--icon-size: 30px;
					margin: 0 0 var(--space-m);
					text-align: right;
				}

				#container {
					display: flex;
					align-items: center;
					justify-content: space-between;
					gap: var(--section-split-gap, var(--space-xl));
				}

				#left {
					flex-shrink: 0;
				}

				#left {
					width: 43%;
				}

				#right {
					width: 55%;
				}

				#headline {
				}

				#text {
				}

				mark {
					color: var(--primary-normal);
					background: none;
				}

				#img,
				#video {
					max-height: 480px;
					object-fit: contain;
					max-width: 100%;
					width: 550px;
					border-radius: var(--border-radius-l);
					box-shadow: var(--section-split-illustration-box-shadow, none);
				}

				@media (max-width: 800px) {
					#container,
					:host([flip]) #container {
						flex-direction: column;
						text-align: center;
					}

					#left,
					#right {
						width: 100%;
					}
				}
			`
		];
	}

	@property({ type: String }) headline?: string;
	@property({ type: String }) text?: string;
	@property({ type: Object }) icon?: Icon;
	@property({ type: String }) illustration?: string;
	@property({ type: Boolean, reflect: true }) flip = false;
	@property({ type: Boolean, reflect: true }) hero = false;
	@property({ type: Boolean, reflect: true }) lazy = false;
	@property({ type: Boolean, reflect: true }) transition = false;

	renderLeft() {
		const { headline, text, icon } = this;
		return html`
			${when(icon, () => html`<bs-icon id="icon" size="huge" .icon="${icon}"></bs-icon>`)}
			${when(headline, () => html`<bs-title id="headline" size="huge" margin>${unsafeHTML(headline)}</bs-title>`)}
			${when(text, () => html`<bs-text id="text" size="huge">${unsafeHTML(text)}</bs-text>`)}
			<slot name="left"></slot>
		`;
	}

	renderRight() {
		const { illustration, transition, lazy } = this;
		return html`
			${when(
				illustration,
				() => html` <bs-intersection-flip ?transition="${transition}">
					${when(
						illustration != null && isVideo(illustration),
						() => html`<video id="video" autoplay loop muted playsinline alt="Illustration" src="${illustration}" />`,
						() =>
							html`
								<img
									id="img"
									alt="Illustration"
									src="${illustration}"
									loading="${ifDefined(lazy ? "lazy" : undefined)}"
								/>
							`
					)}
				</bs-intersection-flip>`
			)}
			<slot name="right"></slot>
		`;
	}

	renderContent() {
		return html`
			<div id="left">${this.renderLeft()}</div>
			<div id="right">${this.renderRight()}</div>
		`;
	}
}
